import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, useLoadScript, MarkerF } from '@react-google-maps/api';
import Autocomplete from "react-google-autocomplete";
import { latitude, longitude } from '../../config/utils/constants';

// props req
// 1. pointerPosition : array of positions like [{lat:'',lng:''}] as we are mapping it to display pointers
// 2. showAutoComplete: boolean true to show autocomplete input false to hide.
// 3. handleUpdatedPositions: a function sending values to the parent component about the updated positions based on drag-end event of the pointers.
// 4. handlePlaceAutoComplete: a function sending values to the parent component about the search result being clicked.

const GoogleMaps = ({
    zoom,
    pointerPositions,
    showAutoComplete = false,
    handleUpdatedPositions,
    handlePlaceAutoComplete,
    mapLoading,
    setMapLoading,
    isDraggable = false,
    defaultAutoCompleteValue = null,
    autoCompleteValueEmptyHandler,
    onManualAddressEntry,
}) => {


    // Default coordinates for London, England
    const defaultCoordinates = { lat: latitude, lng: longitude }

    const [defaultPosition, setDefaultPosition] = useState(defaultCoordinates)
    const [positions, setPositions] = useState(pointerPositions || []);
    // Track whether a Google suggestion was selected so we can distinguish manual entry
    const placeSelectedRef = useRef(false);

    // Define default center position
    useEffect(() => {
        if (pointerPositions && pointerPositions.length > 0) {
            if (pointerPositions?.length == 1 && pointerPositions[0]?.lat == 0) {
                setPositions([])
                setDefaultPosition(defaultCoordinates);
                return
            } else {
                setDefaultPosition(pointerPositions[0]);
                setPositions(pointerPositions);
                return
            }
        } else {
            setPositions([])
            setDefaultPosition(defaultCoordinates);
        }
    }, [pointerPositions])


    // Load Google Maps script
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API,
        libraries: ['places'],
    });

    useEffect(() => {
        if (mapLoading && isLoaded) {
            setMapLoading(false);
        }
    }, [isLoaded])


    if (loadError) {
        if (mapLoading) {
            setMapLoading(false)
        }
        return <div>Error loading maps</div>;
    }

    if (!isLoaded) {
        return (
            <div className='flex items-center justify-center w-full h-full'>Loading maps...</div>
        )
    }


    const markerDragger = (event, index) => {
        const newPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        };
        setPositions((prev) => {
            const updatedPositions = [...prev];
            updatedPositions[index] = newPosition;
            handleUpdatedPositions(updatedPositions);
            return updatedPositions;
        });
    }

    const placeAutoCompleteHandler = async (event) => {
        placeSelectedRef.current = true;
        if (event?.formatted_address || event?.geometry) {
            const { formatted_address, geometry: { location: { lat, lng } } } = event;
            const latitude = lat();
            const longitude = lng();
            handlePlaceAutoComplete({ formatted_address, latitude, longitude });
        }
        // based on these lat lng i will call an api to get the sp and after getting them
        // i will update the state of positions so the markers will be mapped pointing to those lat lng.
    }

    // a function to tell parent that the autocomplete field is empty so we can call default
    // coordinates again to show the current location's data.
    const onChangePlaceAutoCompleteHandler = (e) => {
        // Reset the flag whenever the user types so we can detect manual entry
        placeSelectedRef.current = false;
        if (e?.target?.value?.length == 0) {
            autoCompleteValueEmptyHandler && autoCompleteValueEmptyHandler(true)
        }
    }

    // Allow user to confirm a manually typed address via Enter key or by leaving the field
    const onManualEntryBlur = (e) => {
        const value = e?.target?.value?.trim();
        if (!placeSelectedRef.current && value && onManualAddressEntry) {
            onManualAddressEntry(value);
        }
    }

    const onManualEntryKeyDown = (e) => {
        if (e.key === 'Enter') {
            const value = e?.target?.value?.trim();
            if (!placeSelectedRef.current && value && onManualAddressEntry) {
                onManualAddressEntry(value);
            }
        }
    }

    return (
        <div className='w-full h-full relative'>
            {
                showAutoComplete && isLoaded &&
                <div className='absolute top-5 right-5 z-20 w-64'>
                    <Autocomplete
                        apiKey={process.env.REACT_APP_GOOGLE_MAP_API}
                        onPlaceSelected={placeAutoCompleteHandler}
                        libraries={['places']}
                        options={{
                            types: [], // Can specify types like 'address', 'geocode', '(cities)', etc.
                            // componentRestrictions: { country: 'pk' }, // Restrict search to a specific country if needed
                        }}
                        className='w-full h-12 p-3 outline-none rounded-md'
                        placeholder='Enter a location'
                        onChange={onChangePlaceAutoCompleteHandler}
                        onBlur={onManualEntryBlur}
                        onKeyDown={onManualEntryKeyDown}
                        defaultValue={defaultAutoCompleteValue && defaultAutoCompleteValue}
                    />
                </div>
            }

            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '400px' }}
                zoom={zoom ? zoom : 10}
                center={defaultPosition}
                options={{
                    fullscreenControl: false, // Hide the full-screen control
                    mapTypeControl: false,
                    streetViewControl: true,
                    zoomControl: true,
                }}
            >
                {positions &&
                    positions.map((pos, index) => (
                        <MarkerF
                            draggable={isDraggable}
                            onDragEnd={(event) => isDraggable && markerDragger(event, index)}
                            key={index}
                            position={pos}
                        />
                    ))}
            </GoogleMap>
        </div>
    );
};

export default GoogleMaps;
